import usePrivateLink from "../../config/usePrivateLink";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import {
  appBankStatusUpdate,
  BankAdd,
  BankDelete,
  BankList,
  BankUpdate,
} from "../../../api/BankService";
import { closeModal, SweetAlertShow, LoadingDiv } from "../../master/Master";
import Swal from "sweetalert2";
import BankLayout from "../../Accounts/BankLayout";
import { imgBaseUrl } from "../../../api/api";
import { Link } from "react-router-dom";

export default function BankAccount() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [Banks, setBanks] = useState([]);
  const [modalData, setModelData] = useState({});
  const [qrImage, setQrImage] = useState(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [selectedQr, setSelectedQr] = useState(null);

  /* =========================================================
     QR PREVIEW URL
  ========================================================= */

  useEffect(() => {
    if (!qrImage) {
      setQrPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(qrImage);
    setQrPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [qrImage]);

  /* =========================================================
     GET BANKS
  ========================================================= */

  const getBusinessBanks = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     ADD
  ========================================================= */

  const BankCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await BankAdd(formData);

      if (response?.data?.status) {
        closeModal("BankAdd");

        e.target.reset();
        setQrImage(null);

        SweetAlertShow("success", "Successfully", response?.data?.message);

        getBusinessBanks();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     APP STATUS
  ========================================================= */

  const handleAppStatusUpdate = async (item) => {
    const newStatus = Number(item?.is_app_show) === 1 ? "0" : "1";

    try {
      const response = await appBankStatusUpdate({
        id: item?.id,
        is_app_show: newStatus,
      });

      if (response?.data?.status) {
        setBanks((prev) =>
          prev.map((bank) =>
            bank.id === item.id
              ? {
                ...bank,
                is_app_show: newStatus,
              }
              : bank,
          ),
        );

        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    }
  };

  /* =========================================================
     UPDATE
  ========================================================= */

  const updateBankDetails = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    const qrImg = formData.get("qr_img");
    const upiId = formData.get("upi_id")?.trim();

    const hasExistingQr = !!modalData?.qr_img;
    const hasNewQr = qrImg instanceof File && qrImg.size > 0;

    if ((hasExistingQr || hasNewQr) && !upiId) {
      SweetAlertShow(
        "warning",
        "Validation",
        "UPI ID is required when QR Image is available.",
      );
      return;
    }

    setLoading(true);

    try {
      const response = await BankUpdate(formData);

      if (response?.data?.status) {
        closeModal("BankUpdate");

        e.target.reset();
        setQrImage(null);

        SweetAlertShow("success", "Successfully", response?.data?.message);

        getBusinessBanks();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Account?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      const response = await BankDelete(id);

      if (response?.data?.status === true) {
        SweetAlertShow("success", "Successfully", response?.data?.message);

        getBusinessBanks();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     ACCOUNT TYPE
  ========================================================= */

  const getAccountType = (item) => {
    const account = String(item?.account_no || "").toLowerCase();

    const owner = String(item?.owner_name || "").toLowerCase();

    if (account === "cash") {
      return {
        label: "Cash",
        icon: "ri-cash-line",
        className: "account-type-cash",
      };
    }

    if (
      account.includes("phonepe") ||
      account.includes("paytm") ||
      item?.upi_id
    ) {
      return {
        label: "UPI / Payment",
        icon: "ri-smartphone-line",
        className: "account-type-upi",
      };
    }

    if (account.includes("card") || owner === "card") {
      return {
        label: "Card",
        icon: "ri-bank-card-line",
        className: "account-type-card",
      };
    }

    if (item?.ifsc_code) {
      return {
        label: "Bank Account",
        icon: "ri-bank-line",
        className: "account-type-bank",
      };
    }

    return {
      label: "Other",
      icon: "ri-wallet-3-line",
      className: "account-type-other",
    };
  };

  /* =========================================================
     SUMMARY
  ========================================================= */

  const summary = useMemo(() => {
    const totalBalance = Banks.reduce(
      (sum, item) => sum + Number(item?.amount || 0),
      0,
    );

    const appVisible = Banks.filter(
      (item) => Number(item?.is_app_show) === 1,
    ).length;

    const qrAccounts = Banks.filter((item) => item?.qr_img).length;

    const upiAccounts = Banks.filter((item) => item?.upi_id).length;

    return {
      totalBalance,
      appVisible,
      qrAccounts,
      upiAccounts,
    };
  }, [Banks]);

  useEffect(() => {
    getBusinessBanks();
  }, []);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid bank-container">
              <div className="row">
                <BankLayout />

                <div className="col-12">
                  {/* =================================================
                      PAGE HEADER
                  ================================================= */}

                  <div className="accounts-header">
                    <div className="accounts-title">
                      <div className="accounts-main-icon">
                        <i className="ri-wallet-3-line"></i>
                      </div>

                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h3 className="mb-0 fw-bold">Accounts & Payments</h3>

                          <span className="badge bg-label-primary rounded-pill">
                            {Banks.length} Accounts
                          </span>
                        </div>

                        <p className="text-muted mb-0 mt-1">
                          Manage bank accounts, cash, cards and digital payment
                          methods.
                        </p>
                      </div>
                    </div>

                    {hasAccess("/account/bank/add") && (
                      <button
                        type="button"
                        className="btn btn-primary px-4"
                        data-bs-toggle="modal"
                        data-bs-target="#BankAdd"
                      >
                        <i className="ri-add-line me-1"></i>
                        Add Account
                      </button>
                    )}
                  </div>

                  {/* =================================================
                      SUMMARY
                  ================================================= */}

                  <div className="row g-3 mb-4">
                    <div className="col-xl-3 col-md-6">
                      <div className="account-summary">
                        <div className="summary-icon bg-label-primary">
                          <i className="ri-money-rupee-circle-line"></i>
                        </div>

                        <div>
                          <small className="text-muted">Total Balance</small>

                          <h4 className="mb-0 fw-bold">
                            ₹{" "}
                            {summary.totalBalance.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </h4>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-3 col-md-6">
                      <div className="account-summary">
                        <div className="summary-icon bg-label-success">
                          <i className="ri-smartphone-line"></i>
                        </div>

                        <div>
                          <small className="text-muted">App Visible</small>

                          <h4 className="mb-0 fw-bold">
                            {summary.appVisible}
                            <span className="text-muted fs-6 fw-normal">
                              {" "}
                              / {Banks.length}
                            </span>
                          </h4>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-3 col-md-6">
                      <div className="account-summary">
                        <div className="summary-icon bg-label-info">
                          <i className="ri-link-m"></i>
                        </div>

                        <div>
                          <small className="text-muted">UPI Enabled</small>

                          <h4 className="mb-0 fw-bold">
                            {summary.upiAccounts}
                          </h4>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-3 col-md-6">
                      <div className="account-summary">
                        <div className="summary-icon bg-label-warning">
                          <i className="ri-qr-code-line"></i>
                        </div>

                        <div>
                          <small className="text-muted">QR Available</small>

                          <h4 className="mb-0 fw-bold">{summary.qrAccounts}</h4>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                      MAIN TABLE
                  ================================================= */}

                  <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-transparent border-0 px-4 pt-4 pb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <h5 className="fw-bold mb-1">
                            <i className="ri-list-check-2 text-primary me-2"></i>
                            Account List
                          </h5>

                          <p className="text-muted small mb-0">
                            Bank accounts and payment methods
                          </p>
                        </div>

                        <div className="account-list-info">
                          <i className="ri-information-line me-1"></i>
                          Click QR to view payment code
                        </div>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      {Banks.length ? (
                        <div className="table-responsive">
                          <table className="table align-middle mb-0 accounts-table">
                            <thead>
                              <tr>
                                <th className="ps-4">Account</th>
                                <th>Type</th>
                                <th>Bank Details</th>
                                <th>Balance</th>
                                <th>UPI / QR</th>
                                <th>App</th>
                                <th className="text-end pe-4">Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {Banks.map((item, index) => {
                                const type = getAccountType(item);

                                const appVisible =
                                  Number(item?.is_app_show) === 1;

                                return (
                                  <tr key={item?.id ?? index}>
                                    {/* ACCOUNT */}
                                    <td className="ps-4">
                                      <div className="account-cell">
                                        <div
                                          className={`account-avatar ${type.className}`}
                                        >
                                          <i className={type.icon}></i>
                                        </div>

                                        <div className="account-name-wrapper">
                                          <div className="account-name">
                                            <Link
                                              to={`/settings/bank/transactions/${item?.id}`}
                                              className="account-name text-body"
                                            >
                                              {item?.account_no ||
                                                "Unnamed Account"}
                                            </Link>
                                          </div>

                                          <div className="account-owner">
                                            <i className="ri-user-3-line me-1"></i>
                                            {item?.owner_name || "No owner"}
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* TYPE */}
                                    <td>
                                      <span
                                        className={`account-type ${type.className}`}
                                      >
                                        <i className={type.icon}></i>

                                        {type.label}
                                      </span>
                                    </td>

                                    {/* BANK DETAILS */}
                                    <td>
                                      {item?.ifsc_code || item?.branch ? (
                                        <div className="bank-details">
                                          {item?.ifsc_code && (
                                            <div className="ifsc-text">
                                              <span>IFSC</span>

                                              <strong>{item.ifsc_code}</strong>
                                            </div>
                                          )}

                                          {item?.branch && (
                                            <div className="branch-text">
                                              <i className="ri-map-pin-line"></i>

                                              {item.branch}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted small">
                                          No bank details
                                        </span>
                                      )}
                                    </td>

                                    {/* BALANCE */}
                                    <td>
                                      <div className="balance-cell">
                                        <span className="balance-label">
                                          Current Balance
                                        </span>

                                        <strong>
                                          ₹{" "}
                                          {Number(
                                            item?.amount || 0,
                                          ).toLocaleString("en-IN", {
                                            maximumFractionDigits: 2,
                                          })}
                                        </strong>
                                      </div>
                                    </td>

                                    {/* UPI / QR */}
                                    <td>
                                      {item?.upi_id || item?.qr_img ? (
                                        <div className="payment-cell">
                                          {item?.upi_id && (
                                            <div className="upi-id">
                                              <i className="ri-at-line"></i>

                                              <span>{item.upi_id}</span>
                                            </div>
                                          )}

                                          {item?.qr_img && (
                                            <button
                                              type="button"
                                              className="qr-view-btn"
                                              onClick={() =>
                                                setSelectedQr(item)
                                              }
                                              data-bs-toggle="modal"
                                              data-bs-target="#QRPreview"
                                            >
                                              <i className="ri-qr-code-line"></i>
                                              View QR
                                            </button>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted small">
                                          No UPI / QR
                                        </span>
                                      )}
                                    </td>

                                    {/* APP */}
                                    <td>
                                      <div className="app-column">
                                        <div className="form-check form-switch mb-0">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={appVisible}
                                            onChange={() =>
                                              handleAppStatusUpdate(item)
                                            }
                                          />
                                        </div>

                                        <span
                                          className={
                                            appVisible
                                              ? "app-visible"
                                              : "app-hidden"
                                          }
                                        >
                                          {appVisible ? "Visible" : "Hidden"}
                                        </span>
                                      </div>
                                    </td>

                                    {/* ACTION */}
                                    <td className="text-end pe-4">
                                      <div className="action-buttons">
                                        {hasAccess("/settings/bank/update") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-label-primary"
                                            title="Edit"
                                            data-bs-toggle="modal"
                                            data-bs-target="#BankUpdate"
                                            onClick={() => {
                                              setModelData(item);
                                              setQrImage(null);
                                            }}
                                          >
                                            <i className="ri-pencil-line"></i>
                                          </button>
                                        )}

                                        {hasAccess("/settings/bank/delete") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-label-danger"
                                            title="Delete"
                                            onClick={() =>
                                              handleDelete(item?.id)
                                            }
                                          >
                                            <i className="ri-delete-bin-6-line"></i>
                                          </button>
                                        )}
                                        {hasAccess("/settings/bank/transactions") && (

                                          <Link
                                            to={`/settings/bank/transactions/${item?.id}`}
                                            className="btn btn-sm btn-icon btn-label-info"
                                            title="View Transactions"
                                          >
                                            <i className="ri-file-list-3-line"></i>
                                          </Link>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="empty-account-state">
                          <div className="empty-account-icon">
                            <i className="ri-wallet-3-line"></i>
                          </div>

                          <h5 className="fw-bold">No Accounts Added</h5>

                          <p className="text-muted">
                            Add your first bank account, cash account or payment
                            method.
                          </p>

                          {hasAccess("/settings/bank/add") && (
                            <button
                              className="btn btn-primary px-4"
                              data-bs-toggle="modal"
                              data-bs-target="#BankAdd"
                            >
                              <i className="ri-add-line me-1"></i>
                              Add Account
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          QR PREVIEW MODAL
      ========================================================= */}

      <div
        className="modal fade"
        id="QRPreview"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 overflow-hidden">
            <div className="modal-header qr-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="qr-modal-icon">
                  <i className="ri-qr-code-line"></i>
                </div>

                <div>
                  <h5 className="modal-title fw-bold mb-0">Payment QR Code</h5>

                  <small className="text-muted">Scan to make a payment</small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body text-center p-4">
              {selectedQr?.qr_img && (
                <div className="qr-large-wrapper">
                  <img
                    src={`${imgBaseUrl}${selectedQr.qr_img}`}
                    alt="Payment QR"
                    className="qr-large-image"
                  />
                </div>
              )}

              <div className="qr-account-info mt-4">
                <h6 className="fw-bold mb-1">{selectedQr?.account_no}</h6>

                <div className="text-muted small">{selectedQr?.owner_name}</div>

                {selectedQr?.upi_id && (
                  <div className="qr-upi-id mt-3">
                    <span className="text-muted">UPI ID</span>

                    <strong>{selectedQr.upi_id}</strong>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-label-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          ADD ACCOUNT MODAL
      ========================================================= */}

      <div className="modal fade" id="BankAdd" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 overflow-hidden">
            <div className="modal-header account-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="modal-icon bg-label-primary">
                  <i className="ri-wallet-3-line"></i>
                </div>

                <div>
                  <h5 className="fw-bold mb-0">Add Account</h5>

                  <small className="text-muted">
                    Add bank, cash or payment account
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <form onSubmit={BankCreate}>
              <div className="modal-body p-4">
                <div className="form-section-title">
                  <i className="ri-bank-line"></i>
                  Account Details
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="account_no"
                        className="form-control"
                        placeholder="Account"
                        required
                      />

                      <label>Account / Payment Name</label>
                    </div>

                    <small className="text-muted">
                      Example: Cash, Bank A/C, PhonePe, Card etc.
                    </small>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="owner_name"
                        className="form-control text-uppercase"
                        placeholder="Owner"
                      />

                      <label>Account Holder / Owner</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="ifsc_code"
                        className="form-control text-uppercase"
                        placeholder="IFSC"
                      />

                      <label>IFSC Code</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="branch"
                        className="form-control text-uppercase"
                        placeholder="Branch"
                      />

                      <label>Branch</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        name="amount"
                        className="form-control"
                        placeholder="Balance"
                        min="0"
                        step="any"
                      />

                      <label>Opening Balance</label>
                    </div>
                  </div>
                </div>

                <div className="form-section-title mt-4">
                  <i className="ri-qr-code-line"></i>
                  UPI & Payment Details
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="upi_id"
                        className="form-control"
                        placeholder="UPI ID"
                      />

                      <label>UPI ID</label>
                    </div>

                    <small className="text-muted">Example: school@aubank</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Payment QR Code
                    </label>

                    <input
                      type="file"
                      name="qr_img"
                      className="form-control"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={(e) => setQrImage(e.target.files?.[0] || null)}
                    />

                    {qrImage && (
                      <div className="selected-file mt-2">
                        <i className="ri-image-line me-1"></i>
                        {qrImage.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer px-4">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1"></i>
                      Save Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          UPDATE ACCOUNT MODAL
      ========================================================= */}

      <div
        className="modal fade"
        id="BankUpdate"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 overflow-hidden">
            <div className="modal-header account-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="modal-icon bg-label-primary">
                  <i className="ri-edit-box-line"></i>
                </div>

                <div>
                  <h5 className="fw-bold mb-0">Update Account</h5>

                  <small className="text-muted">
                    Update account and payment information
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <form onSubmit={updateBankDetails}>
              <div className="modal-body p-4">
                <input type="hidden" name="id" value={modalData?.id ?? 0} />

                <div className="form-section-title">
                  <i className="ri-bank-line"></i>
                  Account Details
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="account_no"
                        className="form-control"
                        defaultValue={modalData?.account_no || ""}
                        required
                      />

                      <label>Account / Payment Name</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="owner_name"
                        className="form-control text-uppercase"
                        defaultValue={modalData?.owner_name || ""}
                      />

                      <label>Account Holder / Owner</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="ifsc_code"
                        className="form-control text-uppercase"
                        defaultValue={modalData?.ifsc_code || ""}
                      />

                      <label>IFSC Code</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="branch"
                        className="form-control text-uppercase"
                        defaultValue={modalData?.branch || ""}
                      />

                      <label>Branch</label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        name="amount"
                        className="form-control"
                        defaultValue={modalData?.amount ?? ""}
                        min="0"
                        step="any"
                      />

                      <label>Current Balance</label>
                    </div>
                  </div>
                </div>

                <div className="form-section-title mt-4">
                  <i className="ri-qr-code-line"></i>
                  UPI & QR Payment
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        name="upi_id"
                        className="form-control"
                        defaultValue={modalData?.upi_id || ""}
                        required={
                          !!modalData?.qr_img || qrImage instanceof File
                        }
                      />

                      <label>
                        UPI ID
                        {(modalData?.qr_img || qrImage instanceof File) && (
                          <span className="text-danger"> *</span>
                        )}
                      </label>
                    </div>

                    {(modalData?.qr_img || qrImage instanceof File) && (
                      <small className="text-muted mt-2 d-block">
                        <i className="ri-information-line me-1"></i>
                        Required because a QR code is configured.
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Replace QR Code
                    </label>

                    <input
                      type="file"
                      name="qr_img"
                      className="form-control"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={(e) => setQrImage(e.target.files?.[0] || null)}
                    />
                  </div>
                </div>

                {/* QR PREVIEW */}

                {(qrImage || modalData?.qr_img) && (
                  <div className="edit-qr-preview mt-4">
                    <div>
                      <span className="text-muted small">
                        {qrImage ? "New QR Code" : "Current QR Code"}
                      </span>

                      <h6 className="fw-bold mb-0 mt-1">Payment QR</h6>
                    </div>

                    <div className="edit-qr-image-wrapper">
                      <img
                        src={
                          qrPreviewUrl || `${imgBaseUrl}${modalData?.qr_img}`
                        }
                        alt="QR Preview"
                        className="edit-qr-image"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer px-4">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1"></i>
                      Update Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          CSS
      ========================================================= */}

      <style>{`

        .bank-container {
          padding-top: 18px;
          padding-bottom: 30px;
        }

        /* ===============================
           HEADER
        =============================== */

        .accounts-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          margin-bottom: 22px;
        }

        .accounts-title {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .accounts-main-icon {
          width: 52px;
          height: 52px;
          min-width: 52px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(var(--bs-primary-rgb), .11);
          color: var(--bs-primary);
          font-size: 25px;
        }

        /* ===============================
           SUMMARY
        =============================== */

        .account-summary {
          height: 100%;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 17px;
          border-radius: 15px;
          border: 1px solid var(--bs-border-color);
          background: var(--bs-card-bg, var(--bs-body-bg));
          transition: .2s ease;
        }

        .account-summary:hover {
          transform: translateY(-2px);
          box-shadow: 0 7px 20px rgba(0,0,0,.05);
        }

        .summary-icon {
          width: 44px;
          height: 44px;
          min-width: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        /* ===============================
           TABLE
        =============================== */

        .accounts-table {
          min-width: 1250px;
        }

        .accounts-table thead th {
          padding: 13px 12px;
          background: rgba(var(--bs-secondary-rgb), .035);
          border-bottom: 1px solid var(--bs-border-color);
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .5px;
          color: var(--bs-secondary-color);
          white-space: nowrap;
        }

        .accounts-table tbody td {
          padding: 15px 12px;
          border-bottom: 1px solid var(--bs-border-color-translucent);
        }

        .accounts-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .accounts-table tbody tr {
          transition: .15s ease;
        }

        .accounts-table tbody tr:hover {
          background: rgba(var(--bs-primary-rgb), .025);
        }

        /* ===============================
           ACCOUNT CELL
        =============================== */

        .account-cell {
          display: flex;
          align-items: center;
          gap: 11px;
          min-width: 240px;
        }

        .account-avatar {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 19px;
        }

        .account-name {
          max-width: 230px;
          font-weight: 700;
          font-size: 13px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .account-owner {
          margin-top: 3px;
          color: var(--bs-secondary-color);
          font-size: 10px;
          max-width: 230px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ===============================
           ACCOUNT TYPES
        =============================== */

        .account-type {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 9px;
          border-radius: 7px;
          font-size: 10px;
          font-weight: 700;
          white-space: nowrap;
        }

        .account-type-cash {
          background: rgba(var(--bs-success-rgb), .10);
          color: var(--bs-success);
        }

        .account-type-bank {
          background: rgba(var(--bs-primary-rgb), .10);
          color: var(--bs-primary);
        }

        .account-type-upi {
          background: rgba(var(--bs-info-rgb), .10);
          color: var(--bs-info);
        }

        .account-type-card {
          background: rgba(var(--bs-warning-rgb), .12);
          color: var(--bs-warning-text-emphasis, #b07800);
        }

        .account-type-other {
          background: rgba(var(--bs-secondary-rgb), .09);
          color: var(--bs-secondary-color);
        }

        /* ===============================
           BANK DETAILS
        =============================== */

        .bank-details {
          min-width: 145px;
        }

        .ifsc-text {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 11px;
        }

        .ifsc-text span {
          color: var(--bs-secondary-color);
          font-size: 9px;
          font-weight: 700;
        }

        .ifsc-text strong {
          font-family: monospace;
          letter-spacing: .3px;
        }

        .branch-text {
          margin-top: 5px;
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 10px;
          color: var(--bs-secondary-color);
        }

        /* ===============================
           BALANCE
        =============================== */

        .balance-cell {
          min-width: 125px;
        }

        .balance-label {
          display: block;
          font-size: 9px;
          color: var(--bs-secondary-color);
          margin-bottom: 2px;
        }

        .balance-cell strong {
          font-size: 14px;
          color: var(--bs-success);
        }

        /* ===============================
           UPI
        =============================== */

        .payment-cell {
          min-width: 150px;
        }

        .upi-id {
          display: flex;
          align-items: center;
          gap: 5px;
          max-width: 170px;
          font-size: 11px;
          font-weight: 600;
        }

        .upi-id i {
          color: var(--bs-info);
        }

        .upi-id span {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .qr-view-btn {
          margin-top: 6px;
          padding: 3px 7px;
          border: 0;
          border-radius: 6px;
          background: rgba(var(--bs-info-rgb), .10);
          color: var(--bs-info);
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
        }

        .qr-view-btn:hover {
          background: rgba(var(--bs-info-rgb), .18);
        }

        /* ===============================
           APP
        =============================== */

        .app-column {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .app-column .form-check-input {
          cursor: pointer;
        }

        .app-visible,
        .app-hidden {
          font-size: 10px;
          font-weight: 700;
        }

        .app-visible {
          color: var(--bs-success);
        }

        .app-hidden {
          color: var(--bs-secondary-color);
        }

        /* ===============================
           ACTIONS
        =============================== */

        .action-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 6px;
        }

        .action-buttons .btn {
          width: 33px;
          height: 33px;
          border-radius: 8px;
        }

        /* ===============================
           EMPTY
        =============================== */

        .empty-account-state {
          min-height: 340px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          padding: 40px;
        }

        .empty-account-icon {
          width: 75px;
          height: 75px;
          border-radius: 22px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 17px;
          background: rgba(var(--bs-primary-rgb), .08);
          color: var(--bs-primary);
          font-size: 35px;
        }

        .account-list-info {
          padding: 6px 10px;
          border-radius: 7px;
          background: rgba(var(--bs-info-rgb), .07);
          color: var(--bs-info);
          font-size: 10px;
          font-weight: 600;
        }

        /* ===============================
           MODAL
        =============================== */

        .account-modal-header {
          padding: 18px 22px;
          border-bottom: 1px solid var(--bs-border-color);
          background: rgba(var(--bs-primary-rgb), .025);
        }

        .modal-icon {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 20px;
        }

        .form-section-title {
          display: flex;
          align-items: center;
          gap: 7px;
          color: var(--bs-primary);
          font-size: 12px;
          font-weight: 700;
          padding-bottom: 9px;
          margin-bottom: 15px;
          border-bottom: 1px dashed var(--bs-border-color);
        }

        .selected-file {
          padding: 7px 9px;
          border-radius: 7px;
          background: rgba(var(--bs-success-rgb), .08);
          color: var(--bs-success);
          font-size: 10px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ===============================
           QR MODAL
        =============================== */

        .qr-modal-header {
          border-bottom: 1px solid var(--bs-border-color);
          background: rgba(var(--bs-info-rgb), .035);
        }

        .qr-modal-icon {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(var(--bs-info-rgb), .12);
          color: var(--bs-info);
          font-size: 21px;
        }

        .qr-large-wrapper {
          display: inline-flex;
          padding: 14px;
          border-radius: 17px;
          background: #fff;
          box-shadow: 0 7px 25px rgba(0,0,0,.10);
        }

        .qr-large-image {
          width: 260px;
          height: 260px;
          object-fit: contain;
          display: block;
        }

        .qr-account-info {
          padding: 14px;
          border-radius: 12px;
          background: rgba(var(--bs-secondary-rgb), .045);
        }

        .qr-upi-id {
          display: inline-flex;
          flex-direction: column;
          gap: 2px;
          padding: 8px 15px;
          border-radius: 9px;
          background: rgba(var(--bs-info-rgb), .08);
        }

        .qr-upi-id span {
          font-size: 9px;
        }

        .qr-upi-id strong {
          color: var(--bs-info);
          font-size: 13px;
        }

        /* ===============================
           EDIT QR
        =============================== */

        .edit-qr-preview {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          padding: 14px;
          border: 1px dashed var(--bs-border-color);
          border-radius: 12px;
          background: rgba(var(--bs-secondary-rgb), .025);
        }

        .edit-qr-image-wrapper {
          padding: 5px;
          background: #fff;
          border-radius: 9px;
        }

        .edit-qr-image {
          width: 85px;
          height: 85px;
          object-fit: contain;
          display: block;
        }

        /* ===============================
           MOBILE
        =============================== */

        @media (max-width: 767.98px) {

          .accounts-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .accounts-header .btn {
            width: 100%;
          }

          .accounts-title {
            align-items: flex-start;
          }

          .accounts-main-icon {
            width: 45px;
            height: 45px;
            min-width: 45px;
          }

          .accounts-title h3 {
            font-size: 20px;
          }

          .qr-large-image {
            width: 220px;
            height: 220px;
          }

        }

      `}</style>
    </>
  );
}
